// Сборка ссылки сохранённого вида из его `url_state`. Вынесено из вёрстки под
// тесты: после переработки сайд-бара дата/глубина/вид/наложения/изотерма/
// градиенты живут в общем контексте, а вид всё равно обязан восстановить весь
// набор. Наложения (overlays/fleet/waves/showuncat) входят в состояние раздела
// и сериализуются его же writeOverlaysUrl — отдельного ключа нет.
//
// ВИД ПОМНИТ СВОЙ РАЗДЕЛ. До 15.08.2026 сохранять умели только «Карты», и
// ссылка всегда вела на /maps. С кнопкой в «Моделях» это перестало быть
// правдой: у раздела свой набор ключей (модель, снос, траектории HSI, косяки,
// окно свежести), и открывать его картами значило бы терять их молча.
// Вид без пометки — старый, а старые бывают только картами.
//
// ДАТА ПЕРЕСЧИТЫВАЕТСЯ (см. savedViewDate): вид, сохранённый на завтра,
// завтрашним и открывается. Это единственный параметр, который вид не
// восстанавливает как было.

import { mapStateToQuery } from '../mapState'
import { parseFleet } from '../models/fleetSegments'
import { WAVE_STEP_DEFAULT } from '../map/waveContours'
import {
  DEFAULT_MODELS_STATE,
  serializeModelsState,
  type ModelsState,
} from '../models/modelsUrlState'
import { DEFAULT_FRONT_UNIT } from '../maps/frontsMath'
import { savedViewDate } from './savedViewDate'

export type ViewSection = 'maps' | 'models'

/** Ключ раздела в `url_state`. */
export const SECTION_KEY = 'section'

export function savedViewSection(u: Record<string, unknown>): ViewSection {
  return u[SECTION_KEY] === 'models' ? 'models' : 'maps'
}

const bool = (v: unknown) => v === true || v === '1' || v === 1
const numOrNull = (v: unknown) => (v === null || v === undefined || v === '' ? null : Number(v))
const strings = (v: unknown) => (Array.isArray(v) ? (v as string[]) : [])

function mapsQuery(u: Record<string, unknown>, date: string): string {
  return mapStateToQuery({
    layer: String(u.layer ?? 'sst'),
    depth: Number(u.depth ?? 0),
    date,
    species: numOrNull(u.species),
    overlays: strings(u.overlays),
    fleet: parseFleet(typeof u.fleet === 'string' ? u.fleet : null) ?? [],
    waves: bool(u.waves),
    waveStep: typeof u.waveStep === 'number' ? u.waveStep : WAVE_STEP_DEFAULT,
    showUncat: bool(u.showUncat),
    flags: strings(u.flags),
    isobath: numOrNull(u.isobath),
    lat: numOrNull(u.lat),
    lng: numOrNull(u.lng),
    zoom: numOrNull(u.zoom),
    isotherm: numOrNull(u.isotherm),
    isothermSurface: numOrNull(u.isothermSurface),
    isothermSpread: numOrNull(u.isothermSpread),
    fronts: numOrNull(u.fronts),
    frontsUnit: u.frontsUnit === '10km' ? '10km' : '100km',
  })
}

/** `url_state` → состояние «Моделей». Недостающие ключи берут дефолт раздела:
 *  вид, сохранённый до появления тумблера, обязан открываться, а не падать. */
function modelsState(u: Record<string, unknown>, date: string): ModelsState {
  const model = u.model
  return {
    ...DEFAULT_MODELS_STATE,
    model: model === 'hsi' || model === 'upwelling' || model === 'currents'
      ? model
      : DEFAULT_MODELS_STATE.model,
    species: numOrNull(u.species),
    depth: u.depth != null ? Number(u.depth) : DEFAULT_MODELS_STATE.depth,
    date,
    fleet: parseFleet(typeof u.fleet === 'string' ? u.fleet : null) ?? [],
    overlays: strings(u.overlays),
    waves: bool(u.waves),
    waveStep: typeof u.waveStep === 'number' ? u.waveStep : WAVE_STEP_DEFAULT,
    showUncat: bool(u.showUncat),
    flags: strings(u.flags),
    isobath: numOrNull(u.isobath),
    isotherm: numOrNull(u.isotherm),
    isothermSurface: numOrNull(u.isothermSurface),
    isothermSpread: numOrNull(u.isothermSpread),
    fronts: numOrNull(u.fronts),
    frontsUnit: u.frontsUnit === '10km' ? '10km' : DEFAULT_FRONT_UNIT,
    hsiDrift: bool(u.hsiDrift),
    // Подраздел «Комфортной среды». Фолбэк на дефолт даёт спред выше: вид,
    // сохранённый ДО появления подразделов, обязан открываться нынешним
    // индексом, а не пустой картой незнакомого ключа.
    hsi: typeof u.hsi === 'string' && u.hsi ? u.hsi : DEFAULT_MODELS_STATE.hsi,
    // Косяки включены по умолчанию, поэтому проверяем ЯВНОЕ выключение:
    // `bool(undefined)` дал бы «спрятать» всякому виду, сохранённому раньше.
    schools: u.schools === false || u.schools === '0' || u.schools === 0 ? false : true,
  }
}

function modelsQuery(u: Record<string, unknown>, date: string): string {
  const p = serializeModelsState(modelsState(u, date))
  // Центр и зум ключами раздела не владеют (их ведёт useMapView), поэтому
  // дописываются здесь — иначе вид открылся бы на чужом районе.
  for (const [key, value] of [['lng', u.lng], ['lat', u.lat], ['zoom', u.zoom]] as const) {
    const n = numOrNull(value)
    if (n !== null && Number.isFinite(n)) p.set(key, String(n))
  }
  return p.toString()
}

export interface SavedViewLink {
  path: string
  query: string
}

/**
 * Куда ведёт сохранённый вид: путь раздела + строка запроса.
 *
 * `today` передаётся, а не берётся из часов: от него зависит дата вида, и
 * функция обязана оставаться чистой (иначе её нечем проверить).
 */
export function savedViewLink(
  u: Record<string, unknown>,
  createdAt: string | null | undefined,
  today: string,
): SavedViewLink {
  const date = savedViewDate(u, createdAt, today) ?? ''
  const section = savedViewSection(u)
  return section === 'models'
    ? { path: '/models', query: modelsQuery(u, date) }
    : { path: '/maps', query: mapsQuery(u, date) }
}
